import { useEffect, useRef } from 'react';
import useAuth from './useAuth';
import { Logger } from '../utils/logger';
import mcaSocketService, { McaEvent } from '../sockets/mcaSocketService';

const logger = new Logger('useMcaUpdates');

type McaEventType = 'created' | 'updated' | 'deleted';

export const useMcaUpdates = (
  onUpdateFn: (event: { mca: McaEvent['mca']; type: McaEventType }) => void,
  mcaId: string | null = null
): void => {
  const { user } = useAuth();
  const isMounted = useRef(true);
  const subscriptionRef = useRef(false);
  const onUpdate = useRef(onUpdateFn);

  useEffect(() => {
    onUpdate.current = onUpdateFn;
  }, [onUpdateFn]);

  useEffect(() => {
    isMounted.current = true;
    subscriptionRef.current = false;

    if (!user?.id) {
      logger.debug(`Skipping subscription: userId=${user?.id}`);
      return;
    }

    logger.debug(`Setting up subscription for mcaId: ${mcaId || 'all'}`);
    const subscribe = async () => {
      try {
        await mcaSocketService.subscribeTo(
          mcaId,
          { userId: user.id, namespace: 'mca-updates' },
          {
            onCreated: (event) => {
              if (isMounted.current) {
                onUpdate.current({ mca: event.mca, type: 'created' });
              }
            },
            onUpdated: (event) => {
              if (isMounted.current) {
                onUpdate.current({ mca: event.mca, type: 'updated' });
              }
            },
            onDeleted: (event) => {
              if (isMounted.current) {
                onUpdate.current({ mca: event.mca, type: 'deleted' });
              }
            }
          }
        );
        subscriptionRef.current = true;
        logger.debug('Successfully subscribed to MCA updates');
      } catch (error) {
        logger.error('Subscription failed:', error);
        subscriptionRef.current = false;
      }
    };

    subscribe();

    return () => {
      logger.debug(`Cleaning up subscription for mcaId: ${mcaId || 'all'}`);
      isMounted.current = false;

      if (user?.id) {
        mcaSocketService.unsubscribe(mcaId).catch((err) => logger.warn('Unsubscribe error:', err));
      }
      subscriptionRef.current = false;
    };
  }, [mcaId, user?.id]);
};